import { Component } from '@theme/component';

/**
 * A lightweight YouTube facade: renders a poster image plus a play button, and only injects the
 * real `<iframe>` once the visitor asks for it. Keeps the YouTube player off the critical path of
 * pages that embed a video without the visitor ever playing it.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} poster - The poster button that stands in for the player.
 * @property {HTMLImageElement} [posterImage] - The poster thumbnail, when one is rendered.
 *
 * @extends Component<Refs>
 */
class YoutubeFacade extends Component {
  requiredRefs = ['poster'];

  connectedCallback() {
    super.connectedCallback();

    const { posterImage } = this.refs;
    if (!posterImage) return;

    // `error` does not bubble, so the declarative `on:` bindings cannot pick it up.
    posterImage.addEventListener('error', this.#useFallbackThumbnail, { once: true });

    // This module is deferred, so the thumbnail may already have failed before we got here.
    if (posterImage.complete && posterImage.naturalWidth === 0) this.#swapInFallbackThumbnail(posterImage);
  }

  /**
   * Swaps the poster for the deferred `<iframe>`.
   */
  play = () => {
    if (this.hasAttribute('loaded')) return;

    const template = this.querySelector('template');
    const iframe = template?.content.firstElementChild?.cloneNode(true);
    if (!(iframe instanceof HTMLElement)) return;

    this.setAttribute('loaded', '');
    this.refs.poster.replaceWith(iframe);
    iframe.focus();
  };

  /**
   * `maxresdefault.jpg` is missing for videos that were never uploaded in HD. `hqdefault.jpg` is
   * always generated, so fall back to it rather than showing a broken image.
   */
  #useFallbackThumbnail = (/** @type {Event} */ event) => {
    const image = event.currentTarget;
    if (image instanceof HTMLImageElement) this.#swapInFallbackThumbnail(image);
  };

  /** @param {HTMLImageElement} image */
  #swapInFallbackThumbnail(image) {
    const fallback = image.dataset.fallbackSrc;
    if (fallback && image.src !== fallback) image.src = fallback;
  }
}

if (!customElements.get('youtube-facade')) {
  customElements.define('youtube-facade', YoutubeFacade);
}
